import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { NavigationProps } from '@/types/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { usePlaid } from '@/contexts/PlaidContext';
import { db } from '@/firebase';
import { collection, query, where, getDocs, doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RecurringTransaction } from '@/types/transaction';
import { BankAccount } from '@/types/plaid';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Loader2, HelpCircle, Landmark, CreditCard, HandCoins, LineChart, Eye } from 'lucide-react';
import { normalizeCategory } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartConfig,
} from "@/components/ui/chart"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from "recharts"
import { mockRecurringTransactions, mockBankAccounts } from '@/lib/mockData';

// --- User Category Settings (Similar to TransactionsPage) ---
interface UserCategorySetting {
  name: string;
  emoji: string;
}

// Default emojis (can be expanded)
const categoryEmojis: Record<string, string> = {
  FOOD_AND_DRINK: "🍔", 
  TRAVEL: "✈️",
  SHOPPING: "🛒",
  SERVICES: "💼",
  TRANSFER_OUT: "💸",
  TRANSFER_IN: "💵",
  INCOME: "💰",
  RENT_AND_UTILITIES: "🏠",
  GENERAL_MERCHANDISE: "🎁",
  INSURANCE: "🛡️", // Added example
  TELECOMMUNICATION_SERVICES: "📱", // Added example
  OTHER: "🤷",
  UNCATEGORIZED: "🤷",
};

// Account Icons (Similar to TransactionsPage)
const accountTypeIcons: Record<string, React.ElementType> = {
  depository: Landmark,
  credit: CreditCard,
  loan: HandCoins,
  investment: LineChart,
  other: HelpCircle,
};
// --- End Account Icons ---

// --- START MOCK DATA ---
// --- END MOCK DATA ---

const SUBSCRIPTIONS_CACHE_KEY = 'subscriptions_cache';
const SUBSCRIPTIONS_CACHE_EXPIRY = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

interface SubscriptionsPageProps extends NavigationProps {}

export default function SubscriptionsPage({ onNavigate }: SubscriptionsPageProps) {
  const { currentUser } = useAuth();
  const bankAccounts = mockBankAccounts; // <-- Use imported mockBankAccounts
  const functions = getFunctions();

  // State for subscriptions (split into inbound/outbound)
  const [inboundSubscriptions, setInboundSubscriptions] = useState<RecurringTransaction[]>([]);
  const [outboundSubscriptions, setOutboundSubscriptions] = useState<RecurringTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for category settings
  const [userCategorySettings, setUserCategorySettings] = useState(new Map<string, UserCategorySetting>());
  const [isCategorySettingsLoading, setIsCategorySettingsLoading] = useState(true);

  // --- Fetch Subscriptions (Recurring Transactions) ---
  const fetchSubscriptions = useCallback(async (forceRefresh = false) => {
    // --- START MOCK IMPLEMENTATION ---
    console.log("Using MOCK subscription data.");
    setIsLoading(true); // Still show loading briefly for effect
    setError(null);

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500)); 

    // Filter mock data into inbound/outbound
    const allStreams = mockRecurringTransactions
      .filter(stream => stream.predicted_next_date) 
      .sort((a, b) => a.predicted_next_date.localeCompare(b.predicted_next_date)); 

    const inbound = allStreams.filter(s => s.last_amount.amount >= 0); // Inbound >= 0
    const outbound = allStreams.filter(s => s.last_amount.amount < 0); // Outbound < 0 (negative)
    
    setInboundSubscriptions(inbound);
    setOutboundSubscriptions(outbound);
    setIsLoading(false);
    console.log(`Mock data loaded: ${allStreams.length} total streams (${inbound.length} inbound, ${outbound.length} outbound).`);
    // --- END MOCK IMPLEMENTATION ---

    /* --- ORIGINAL IMPLEMENTATION (Commented out) ---
    if (!currentUser || !db) {
      console.log("No user or db instance, skipping subscriptions fetch.");
      setInboundSubscriptions([]);
      setOutboundSubscriptions([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const cacheDocRef = doc(db, 'users', currentUser.uid, 'recurringStreamsCache', 'allStreams');

    // 1. Check Firestore Cache (Use if exists, regardless of age, unless forcing refresh)
    if (!forceRefresh) {
      try {
        console.log("Checking Firestore cache for subscriptions...");
        const docSnap = await getDoc(cacheDocRef);
        if (docSnap.exists()) {
          const cacheData = docSnap.data();
          // **CHANGE: Use cache if it exists, ignore expiry for initial load** 
          if (Array.isArray(cacheData.streams)) {
            const timestamp = cacheData.timestamp as Timestamp; // Still get timestamp for logging
            const cacheAge = Date.now() - timestamp.toMillis();
            console.log(`Using cached subscriptions from Firestore (Cache age: ${Math.round(cacheAge / 1000 / 60)} mins).`);
            const allStreams = cacheData.streams as RecurringTransaction[];
            // Split cached data
            setInboundSubscriptions(allStreams.filter(s => s.last_amount.amount < 0));
            setOutboundSubscriptions(allStreams.filter(s => s.last_amount.amount >= 0));
            setIsLoading(false);
            setError(null);
            return; // Use cached data
          } else {
             console.log("Firestore cache exists but data is invalid. Proceeding to fetch.");
          }
        } else {
           console.log("No subscription cache found in Firestore. Proceeding to fetch.");
        }
      } catch (e) {
        console.error("Error reading subscription cache from Firestore:", e);
        // Proceed to fetch from function if cache read fails
      }
    }

    // 2. Fetch from Cloud Function (if no cache exists OR forceRefresh is true)
    console.log(`Fetching subscriptions via Cloud Function... (Force refresh: ${forceRefresh})`);
    setIsLoading(true); // Set loading true ONLY when actually fetching
    setError(null);
    
    try {
      const getRecurringFn = httpsCallable(functions, 'getRecurringTransactions');
      const result = await getRecurringFn(); 
      const data = result.data as { inflow_streams: RecurringTransaction[], outflow_streams: RecurringTransaction[] };
      
      const allStreams = [...(data.inflow_streams || []), ...(data.outflow_streams || [])]
          .filter(stream => stream.predicted_next_date) 
          .sort((a, b) => a.predicted_next_date.localeCompare(b.predicted_next_date)); 

      // Split fetched data
      const inbound = allStreams.filter(s => s.last_amount.amount < 0);
      const outbound = allStreams.filter(s => s.last_amount.amount >= 0);
      setInboundSubscriptions(inbound);
      setOutboundSubscriptions(outbound);
      console.log(`Fetched ${allStreams.length} total streams (${inbound.length} inbound, ${outbound.length} outbound).`);

      // 3. Update Firestore Cache on successful fetch
      try {
         console.log("Updating Firestore subscription cache...");
         await setDoc(cacheDocRef, {
             streams: allStreams,
             timestamp: serverTimestamp() // Use server timestamp
         });
         console.log("Firestore cache updated.");
      } catch (e) {
          console.error("Error saving subscriptions to Firestore cache:", e);
      }
        
    } catch (error: any) { 
        console.error("Error fetching subscriptions:", error);
        if (error.code && error.message) {
            console.error(`Cloud Function Error (${error.code}): ${error.message}`, error.details);
            setError(`Failed to load subscriptions (${error.code}).`);
        } else {
            setError("Failed to load subscriptions.");
        }
        setInboundSubscriptions([]);
        setOutboundSubscriptions([]);
    } finally {
        setIsLoading(false);
        console.log("Finished fetching subscriptions attempt."); // Renamed log slightly
    }
    --- END ORIGINAL IMPLEMENTATION --- */
  }, [currentUser /* functions - removed dependency */]); // Removed functions dependency
  // --- End Fetch Subscriptions ---

  // --- Fetch User Category Settings (Copied from TransactionsPage) ---
  const fetchUserCategorySettings = useCallback(async () => {
    if (!currentUser || !db) {
      console.log("User or DB not available, skipping category settings fetch.");
      setUserCategorySettings(new Map());
      setIsCategorySettingsLoading(false);
      return;
    }
    console.log("SubscriptionsPage: Fetching user category settings...");
    setIsCategorySettingsLoading(true);
    const settingsMap = new Map<string, UserCategorySetting>();
    try {
      const categoriesCol = collection(db, 'userCategories');
      const q = query(categoriesCol, where("userId", "==", currentUser.uid));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        settingsMap.set(doc.id, { 
          name: data.name || normalizeCategory(doc.id), 
          emoji: data.emoji || '🤷' 
        });
      });
      console.log(`SubscriptionsPage: Fetched ${settingsMap.size} user category settings.`);
      setUserCategorySettings(settingsMap);
    } catch (error) {
      console.error("SubscriptionsPage: Error fetching user category settings:", error);
      setUserCategorySettings(new Map()); 
    } finally {
      setIsCategorySettingsLoading(false);
    }
  }, [currentUser]);
  // --- End Fetch User Category Settings ---

  // --- Initial Data Fetch Effect ---
  useEffect(() => {
    if (currentUser) {
      fetchSubscriptions();
      fetchUserCategorySettings();
      setError(null);
      setUserCategorySettings(new Map());
      setIsCategorySettingsLoading(true);
      // Clear cache on logout
      try {
          const cacheKey = `${SUBSCRIPTIONS_CACHE_KEY}_${currentUser.uid}`;
          localStorage.removeItem(cacheKey);
          console.log("Cleared subscriptions cache on logout.");
      } catch (e) {
           console.error("Error clearing subscriptions cache on logout:", e);
      }
    } else {
      // Reset state on logout
      setInboundSubscriptions([]);
      setOutboundSubscriptions([]);
      setIsLoading(true);
      setError(null);
      setUserCategorySettings(new Map());
      setIsCategorySettingsLoading(true);
    }
  }, [currentUser, fetchSubscriptions, fetchUserCategorySettings]);
  // --- End Initial Data Fetch Effect ---

  // --- Calculate Total Outbound Subscription Cost ---
  const totalOutboundCost = useMemo(() => {
    // Calculate only when not loading and no error
    if (isLoading || error) return 0; 
    return outboundSubscriptions.reduce((sum, sub) => {
      // Only include active subscriptions in the total
      if (sub.is_active) {
        // Outbound amounts are positive, so sum directly
        return sum + (sub.last_amount?.amount || 0);
      }
      return sum;
    }, 0);
  }, [outboundSubscriptions, isLoading, error]);

  const formattedTotalOutboundCost = useMemo(() => {
    // Assuming USD for now if no subscriptions exist to get currency code
    const currencyCode = outboundSubscriptions[0]?.last_amount?.iso_currency_code || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(Math.abs(totalOutboundCost));
  }, [totalOutboundCost, outboundSubscriptions]);
  // --- End Calculate Total ---

  // --- Calculate Active Outbound Subscription Count ---
  const activeOutboundCount = useMemo(() => {
    if (isLoading || error) return 0;
    return outboundSubscriptions.filter(sub => sub.is_active).length;
  }, [outboundSubscriptions, isLoading, error]);
  // --- End Calculate Count ---

  // --- Helper Functions (Adapted from TransactionsPage) ---
  const getEmojiForCategory = useCallback((rawCategoryKey: string | undefined): string => {
    if (!rawCategoryKey) return categoryEmojis['UNCATEGORIZED'];
    const userSetting = userCategorySettings.get(rawCategoryKey);
    if (userSetting?.emoji) {
      return userSetting.emoji;
    }
    return categoryEmojis[rawCategoryKey] || categoryEmojis['UNCATEGORIZED'];
  }, [userCategorySettings]);

  const getAccountInfo = useCallback((plaidAccountId: string) => {
    return bankAccounts.find(acc => acc.plaidAccountId === plaidAccountId);
  }, [bankAccounts]);

  const getAccountIcon = useCallback((accountType: string | undefined): React.ElementType => {
    return accountType ? accountTypeIcons[accountType] || HelpCircle : HelpCircle;
  }, []);

  const formatDisplayDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    try {
        const date = parseISO(dateString + 'T00:00:00'); 
        return format(date, 'MMM d, yyyy'); // e.g., Apr 17, 2025
    } catch (e) {
        console.error("Error formatting display date:", dateString, e);
        return dateString; // Fallback
    }
  };
  // --- End Helper Functions ---

  // TODO: Add state and handlers for viewing details if needed
  // TODO: Add a manual refresh button that calls fetchSubscriptions(true)

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" onClick={() => onNavigate?.('dashboard' as any)}>
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Subscriptions</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>   
          </Breadcrumb>
        </div>
      </header>
      
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        {/* --- Summary Cards Grid --- */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Total Outbound Cost Summary Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Active Recurring Outbound</CardDescription>
              <CardTitle className="text-3xl">
                {isLoading ? (
                  <Skeleton className="h-8 w-32" />
                ) : error ? (
                  <span className="text-destructive">(Error)</span>
                ) : (
                  formattedTotalOutboundCost
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-muted-foreground">
                Estimated monthly cost based on last payment amount.
              </div>
            </CardContent>
          </Card>

          {/* Active Outbound Count Summary Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Active Recurring Subscriptions</CardDescription>
              <CardTitle className="text-3xl">
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : error ? (
                  <span className="text-destructive">(Error)</span>
                ) : (
                  activeOutboundCount
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-muted-foreground">
                Count of active outbound recurring payments.
              </div>
            </CardContent>
          </Card>
        </div>

        {/* --- Outbound Subscriptions Table --- */}
        <Card>
          <CardHeader>
             {/* Add container for Title/Desc and Button */}
             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <CardTitle>Recurring Bills & Subscriptions</CardTitle>
                    <CardDescription>Your regular outgoing payments.</CardDescription>
                </div>
                {/* Refresh Button */} 
                <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => fetchSubscriptions(true)} // Call with forceRefresh=true
                    disabled={isLoading} // Disable while loading
                    className="mt-2 sm:mt-0 sm:ml-4" // Add margin for spacing
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Refreshing...
                        </>
                    ) : (
                        "Refresh Subscriptions"
                    )}
                </Button>
             </div>
          </CardHeader>
          <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6"> {/* Add padding like TransactionsPage */}
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : error ? (
              <div className="flex h-40 items-center justify-center border rounded-md border-dashed p-4 text-destructive">
                Error loading subscriptions: {error}
              </div>
            ) : outboundSubscriptions.length === 0 ? (
              <div className="flex h-40 items-center justify-center border rounded-md border-dashed p-4">
                <div className="flex flex-col items-center gap-2 text-center">
                  <CreditCard className="h-10 w-10 text-muted-foreground" /> 
                  <h3 className="text-lg font-medium">
                    No Outbound Subscriptions Found
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    We couldn't find any recurring bills or subscriptions.
                  </p>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40 hover:bg-muted/40 border-b">
                    <TableHead>Description</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Next Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outboundSubscriptions.map((sub) => {
                    const account = getAccountInfo(sub.account_id);
                    const AccountIcon = getAccountIcon(account?.type);
                    const rawCategory = sub.personal_finance_category?.primary || 'UNCATEGORIZED';
                    const emoji = getEmojiForCategory(rawCategory);
                    const categoryName = normalizeCategory(rawCategory);
                    const nextDateFormatted = formatDisplayDate(sub.predicted_next_date);
                    const lastAmountFormatted = new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: sub.last_amount.iso_currency_code || 'USD',
                    }).format(Math.abs(sub.last_amount.amount)); // Use absolute value for display consistency
                    const statusText = sub.is_active ? 'Active' : 'Inactive';
                    const statusVariant = sub.is_active ? 'success' : 'secondary'; // Use custom 'success' variant or default

                    return (
                      <TableRow key={sub.stream_id}>
                        <TableCell className="font-medium">
                           <div className="flex items-center gap-2">
                              {/* Logo or Fallback Icon */}
                              {sub.logo_url ? (
                                <img src={sub.logo_url} alt={sub.merchant_name || sub.description} className="h-6 w-6 rounded-sm object-contain flex-shrink-0" />
                              ) : (
                                <div className="h-6 w-6 rounded-sm bg-muted flex items-center justify-center text-xs text-muted-foreground flex-shrink-0">
                                   {(sub.merchant_name || sub.description || '?').charAt(0).toUpperCase()}
                                </div>
                              )}
                              {/* Merchant Name or Description */}
                              <span className="truncate">{sub.merchant_name || sub.description}</span>
                           </div>
                        </TableCell>
                        <TableCell>
                          {account ? (
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="flex items-center gap-2 cursor-default">
                                    <AccountIcon className="h-4 w-4 flex-shrink-0" />
                                    <span>{account.name} (••••{account.mask})</span>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" align="start">
                                  <p className="font-semibold">{account.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {account.type}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-sm text-muted-foreground">Unknown</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                             <span className="mr-2 text-base" role="img" aria-label={categoryName}>{emoji}</span>
                             <Badge variant="outline">{categoryName}</Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize text-xs">
                            {sub.frequency?.toLowerCase().replace('_', ' ') || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell>{nextDateFormatted}</TableCell>
                        <TableCell className="text-right">{lastAmountFormatted}</TableCell>
                        <TableCell>
                          <Badge variant={statusVariant as any}>{statusText}</Badge> {/* Cast variant type */} 
                        </TableCell>
                         {/* 
                         <TableCell className="text-right">
                           <Button variant="ghost" size="icon" className="h-7 w-7">
                             <Eye className="h-4 w-4" />
                             <span className="sr-only">View Details</span>
                           </Button>
                         </TableCell>
                         */} 
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* --- Inbound Subscriptions Table --- */}
        <Card>
          <CardHeader>
            <CardTitle>Recurring Income</CardTitle>
            <CardDescription>Your regular incoming payments.</CardDescription>
          </CardHeader>
          <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : error ? (
              <div className="flex h-40 items-center justify-center border rounded-md border-dashed p-4 text-destructive">
                Error loading subscriptions: {error}
              </div>
            ) : inboundSubscriptions.length === 0 ? (
              <div className="flex h-40 items-center justify-center border rounded-md border-dashed p-4">
                <div className="flex flex-col items-center gap-2 text-center">
                  <HandCoins className="h-10 w-10 text-muted-foreground" />
                  <h3 className="text-lg font-medium">
                    No Inbound Subscriptions Found
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    We couldn't find any recurring income streams.
                  </p>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40 hover:bg-muted/40 border-b">
                    <TableHead>Description</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Next Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inboundSubscriptions.map((sub) => {
                    const account = getAccountInfo(sub.account_id);
                    const AccountIcon = getAccountIcon(account?.type);
                    const rawCategory = sub.personal_finance_category?.primary || 'UNCATEGORIZED';
                    const emoji = getEmojiForCategory(rawCategory);
                    const categoryName = normalizeCategory(rawCategory);
                    const nextDateFormatted = formatDisplayDate(sub.predicted_next_date);
                    const lastAmountFormatted = new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: sub.last_amount.iso_currency_code || 'USD',
                    }).format(Math.abs(sub.last_amount.amount));
                    const statusText = sub.is_active ? 'Active' : 'Inactive';
                    const statusVariant = sub.is_active ? 'success' : 'secondary';

                    return (
                      <TableRow key={sub.stream_id}>
                        <TableCell className="font-medium">
                           <div className="flex items-center gap-2">
                              {/* Logo or Fallback Icon */}
                              {sub.logo_url ? (
                                <img src={sub.logo_url} alt={sub.merchant_name || sub.description} className="h-6 w-6 rounded-sm object-contain flex-shrink-0" />
                              ) : (
                                <div className="h-6 w-6 rounded-sm bg-muted flex items-center justify-center text-xs text-muted-foreground flex-shrink-0">
                                   {(sub.merchant_name || sub.description || '?').charAt(0).toUpperCase()}
                                </div>
                              )}
                              {/* Merchant Name or Description */}
                              <span className="truncate">{sub.merchant_name || sub.description}</span>
                           </div>
                        </TableCell>
                        <TableCell>
                          {account ? (
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="flex items-center gap-2 cursor-default">
                                    <AccountIcon className="h-4 w-4 flex-shrink-0" />
                                    <span>{account.name} (••••{account.mask})</span>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" align="start">
                                  <p className="font-semibold">{account.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {account.type}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-sm text-muted-foreground">Unknown</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                             <span className="mr-2 text-base" role="img" aria-label={categoryName}>{emoji}</span>
                             <Badge variant="outline">{categoryName}</Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize text-xs">
                            {sub.frequency?.toLowerCase().replace('_', ' ') || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell>{nextDateFormatted}</TableCell>
                        <TableCell className="text-right">{lastAmountFormatted}</TableCell>
                        <TableCell>
                          <Badge variant={statusVariant as any}>{statusText}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
} 